import { useEffect, useState, useRef, useContext, useCallback } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { styled } from "@mui/material";

import { theme } from "../../../theme";
import CardTemplate from "./template/CardTemplate";
import {
    deleteDevice,
    deleteSimNumber,
    fetchTrackerDetails,
    ftmBackgroundIsChecked,
    trackerActiveAndReceiving,
    trackerDispatch,
    updateDevice,
    updateSimNumber,
    updateTrackerInstalled,
    uploadPackageImage,
} from "../service";
import LoadingSpinner from "./LoadingSpinner";
import { commonSwal } from "../../../components/swalAlert/swalAlert";
import { Context } from "../Provider";
import MarkAsApproved from "./MarkAsApproved";
import { formatTimestampInMap } from "../../../utils";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

/* -------- Tracker Card (Child) -------- */
const TrackerCard = ({ name, image, btnOptions, handleImageUpload, uploading, isComplete, width, timestamp }) => {
    const fileInputRef = useRef(null);

    const handleButtonClick = (btn) => {
        if (uploading && btn.label === "Upload Photo") return;
        if (btn.label === "Upload Photo") fileInputRef.current?.click();
        else btn.action();
    };

    return (
        <div className="card card-body border card-plain border-radius-lg d-flex align-items-center flex-row py-2 mb-3">
            <img
                className={`me-3 mb-0`}
                src={image}
                alt="logo"
                style={{ width: `${width}%` }}
            />
            <h6 className="mb-0">
                {name}{" "}
                {isComplete && (
                    <i
                        className={`material-icons opacity-10 text-${theme.main} ms-2`}
                    >
                        verified
                    </i>
                )}
                {timestamp && (
                    <p className="mb-0 text-sm">{formatTimestampInMap(timestamp)}</p>
                )}
            </h6>
            <div className="d-flex flex-column ms-auto">
                <div className="d-flex gap-2 mt-1">
                    {btnOptions.map((btn, idx) => (
                        <button
                            className={`btn btn-link btn-icon text-${btn.color} btn-sm mb-0 px-0 py-0`}
                            type="button"
                            key={idx}
                            onClick={() => handleButtonClick(btn)}
                            disabled={btn.isDisabled}
                        >
                            <i className="material-icons" style={{ fontSize: "20px" }}>
                                {btn.icon}
                            </i>
                        </button>
                    ))}
                    <VisuallyHiddenInput
                        type="file"
                        ref={fileInputRef}
                        accept="image/png, image/jpeg"
                        onChange={(e) => handleImageUpload(e.target.files)}
                    />
                </div>
            </div>
        </div>
    );
};

/* -------- Main Component -------- */
const EnableGPSTracker = () => {
    const { ftm_id } = useParams();
    const { dispatch } = useContext(Context);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cardHeight, setCardHeight] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Fetch tracker details
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetchTrackerDetails(ftm_id);
            setData(response?.status === "Success" ? response.data || {} : {});
            if(response.data?.ftm_tracker_is_install === 1){
                dispatch({ type: "SET_SHOW_FTM_MAP", showFtmMap: true });
            }
        } catch (err) {
            console.error("Error fetching tracker details:", err);
        } finally {
            setLoading(false);
        }
    }, [dispatch, ftm_id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRefresh = useCallback(() => {
        dispatch({ type: "INCREMENT_REFRESH_COUNT" });
        fetchData();
    }, [dispatch, fetchData]);

    /* ---- common Swal wrapper ---- */
    const showSwal = useCallback(
        ({ notificationType, preConfirm }) =>
            commonSwal({
                notificationType,
                preConfirm,
                isConfirmed: (res) => {
                    if (res?.isConfirmed && res?.value?.status === "Success") {
                        toast.success(res.value.message);
                        handleRefresh();
                    } else if (res?.value?.message) {
                        toast.error(res.value.message);
                    }
                },
            }),
        [handleRefresh]
    );

    // Handlers
    const setupDevice = useCallback(
        (currentId) =>
            showSwal({
                notificationType: "UPDATE_DEVICE",
                preConfirm: (device_id) => {
                    if (!device_id || device_id === currentId) {
                        Swal.showValidationMessage("Invalid Device ID");
                        return;
                    }
                    return updateDevice(ftm_id, device_id);
                },
            }),
        [ftm_id, showSwal]
    );

    const removeDevice = useCallback(
        () => showSwal({ notificationType: "REMOVE_DEVICE", preConfirm: () => deleteDevice(ftm_id) }),
        [ftm_id, showSwal]
    );

    const setupSimNumber = useCallback(
        () =>
            showSwal({
                notificationType: "UPDATE_SIM_NUMBER",
                preConfirm: (sim_number) => {
                    const regex = /^\+447\d{9}$/;

                    if (!sim_number) {
                        Swal.showValidationMessage("Sim number cannot be empty");
                        return;
                    } else if (regex.test(sim_number) === false) {
                        Swal.showValidationMessage("Invalid mobile number format. It should start with +447 followed by 9 digits.");
                        return;
                    }

                    return updateSimNumber(ftm_id, sim_number);
                },
            }),
        [ftm_id, showSwal]
    );

    const removeSimNumber = useCallback(
        () => showSwal({ notificationType: "REMOVE_SIM_NUMBER", preConfirm: () => deleteSimNumber(ftm_id) }),
        [ftm_id, showSwal]
    );

    const dispatchTracker = useCallback(
        () => showSwal({ notificationType: "FTM_TRACKER_DISPATCH", preConfirm: () => trackerDispatch(ftm_id) }),
        [ftm_id, showSwal]
    );

    const trackerInstalled = useCallback(
        () => 
            showSwal({ notificationType: "FTM_TRACKER_INSTALLED", preConfirm: () => {
                dispatch({ type: "SET_SHOW_FTM_MAP", showFtmMap: true });
                return updateTrackerInstalled(ftm_id)
            } 
        }),
        [dispatch, ftm_id, showSwal]
    );

    const activateTracker = useCallback(
        () => showSwal({ notificationType: "FTM_TRACKER_ACTIVE_AND_RECEIVING", preConfirm: () => trackerActiveAndReceiving(ftm_id)}),
        [ftm_id, showSwal]
    );

    const backgroundChecked = useCallback(
        () => showSwal({ notificationType: "FTM_BACKGROUND_CHECK", preConfirm: () => ftmBackgroundIsChecked(ftm_id)}),
        [ftm_id, showSwal]
    );

    const handleImageUpload = useCallback(
        async (files) => {
            if (!files?.length) return;
            try {
                setUploading(true);
                const res = await uploadPackageImage({ ftm_id, package_image: files[0] });
                res.status === "Success" ? toast.success(res.message) : toast.error(res.message);
                handleRefresh();
            } catch (err) {
                console.error("Upload failed:", err);
                toast.error("Upload failed!");
            } finally {
                setUploading(false);
            }
        },
        [ftm_id, handleRefresh]
    );

    /* ---- Cards ---- */
    const cardData = [
        {
            name: data?.ftm_tracker_sim_number ? `OK – ${data?.ftm_tracker_sim_number}` : "Tracking sim number",
            image: "/assets/img/sim-icon.png",
            btnOptions: [
                { label: "Setup Device", icon: "edit", action: setupSimNumber, color: theme.main, isDisabled: data?.ftm_tracker_sim_number },
                { label: "Remove Device", icon: "delete", action: removeSimNumber, color: theme.close_btn, isDisabled: !data?.ftm_tracker_sim_number },
            ],
            isComplete: data?.ftm_tracker_sim_number,
            width: 3,
            timestamp: null,
        },
        {
            name: data?.ftm_device_id || "Device not set up",
            image: "/assets/img/gps_tracker.png",
            btnOptions: [
                { label: "Setup Device", icon: "build", action: () => setupDevice(data?.ftm_device_id), color: theme.main, isDisabled: data?.ftm_enable_gps_external_device === 1 },
                { label: "Remove Device", icon: "delete", action: removeDevice, color: theme.close_btn, isDisabled: data?.ftm_enable_gps_external_device === 0 },
            ],
            isComplete: data?.ftm_enable_gps_external_device === 1,
            width: 3.5,
            timestamp: data?.ftm_device_timestamp,
        },
        {
            name: uploading ? "Uploading tracker package photo..." : "Upload tracker package photo",
            image: "/assets/img/package-icon.png",
            btnOptions: [
                ...(data?.ftm_package_image_url
                    ? [
                        {
                            label: "View Photo",
                            icon: "photo",
                            action: () => window.open(data.ftm_package_image_url, "_blank"),
                            color: theme.main,
                            isDisabled: uploading,
                        },
                    ]
                    : []),
                {
                    label: "Upload Photo",
                    icon: uploading ? "hourglass_top" : "upload",
                    action: () => {},
                    color: !data?.ftm_package_image_url ? theme.main : theme.close_btn,
                    isDisabled: uploading,
                },
            ],
            isComplete: Boolean(data?.ftm_package_image_url),
            width: 4,
            isShowDateAndTime: false,
        },
        {
            name: "Tracker dispatched",
            image: "/assets/img/trackerDispatchenew.png",
            btnOptions: [{ label: "Dispatch", icon: "send", action: dispatchTracker, color: theme.main, isDisabled: data?.ftm_gps_tracker_is_dispatch === 1 }],
            isComplete: data?.ftm_gps_tracker_is_dispatch === 1,
            width: 4,
            timestamp: data?.ftm_tracker_dispatch_timestamp,
        },
        {
            name: "Tracker installation confirmed",
            image: "/assets/img/tracker_installed.png",
            btnOptions: [{ label: "Installed", icon: "send", action: trackerInstalled, color: theme.main, isDisabled: data?.ftm_tracker_is_install === 1 }],
            isComplete: data?.ftm_tracker_is_install === 1,
            width: 3,
            timestamp: data?.ftm_tracker_installed_timestamp,
        },
        {
            name: "Live tracking active",
            image: "/assets/img/receiving.png",
            btnOptions: [{ label: "View Locations", icon: "location_on", action: activateTracker, color: theme.main, isDisabled: data?.ftm_is_gps_fixed_and_receiving_locations === 1 }],
            isComplete: data?.ftm_is_gps_fixed_and_receiving_locations === 1,
            width: 4,
            isShowDateAndTime: false,
        },
        {
            name: "Background checked",
            image: "/assets/img/background_check_V3.png",
            btnOptions: [{ label: "Checked", icon: "send", action: backgroundChecked, color: theme.main, isDisabled: data?.ftm_background_is_check === 1 }],
            isComplete: data?.ftm_background_is_check === 1,
            width: 4,
            isShowDateAndTime: false,
        },
    ];

    const disableApproval =
        data?.ftm_enable_gps_external_device === 1 &&
        Boolean(data?.ftm_package_image_url) &&
        data?.ftm_gps_tracker_is_dispatch === 1 &&
        data?.ftm_is_gps_fixed_and_receiving_locations === 1 && 
        data?.ftm_background_is_check === 1;

    return (
        <CardTemplate title="GPS Tracker Details">
            {loading ? (
                <LoadingSpinner loading={loading} cardHeight={cardHeight} setCardHeight={setCardHeight} />
            ) : (
                <div className="row">
                    {cardData.map((card, idx) => (
                        <TrackerCard
                            key={idx}
                            {...card}
                            handleImageUpload={handleImageUpload}
                            uploading={uploading}
                        />
                    ))}
                </div>
            )}

            <MarkAsApproved approvalProcessKey="IS_APPROVE" isDisabled={disableApproval} />
        </CardTemplate>
    );
};

export default EnableGPSTracker;